import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ReactComponent as BackArrowIcon } from '../../../assets/icons/arrow-left.svg';
import { ReactComponent as SettingIcon } from '../../../assets/icons/setting-icon.svg';
import { useAppBranding } from '../../../config/branding';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { clearByMonthData } from '../../../redux/slices/ByMonthRevenueSlice';
import {
  clearByTransactionData,
  fetchByTransactionAccountFilters,
} from '../../../redux/slices/ByTransactionSlice';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import { clearPayoutData, fetchAccountRevenueTotal } from '../../../redux/slices/PayoutSlice';
import PageTitle from '../../../shared-components/page-title/PageTitle';
import TabToggle from '../../../shared-components/tab-toggle/TabToggle';
import ToFixedValue from '../../../shared-functions/ToFixedValue';
import account from '../../../utils/api/account';

import Devices from './Devices';
import Payout from './Payout';
import PowerConsumedChart from './PowerConsumedChart';
import Revenue from './Revenue';
import RevenueVsPayableChart from './RevenueVsPayableChart';
import SettingAccount from './SettingAccount';
import Stations from './Stations';

interface AccountManagementDataProperties {
  totalDevices: number;
  totalStations: number;
  online: number;
  offline: number;
}

interface AccountDetailsDataProperties {
  accountCustomId: string;
  accountName: string;
  email: string;
}

/**
 * AccountsManagementPayout component for account management payout page.
 */
function AccountsManagementPayout() {
  const branding = useAppBranding();
  const { t, i18n } = useTranslation('account');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { revenueTotal } = useAppSelector((state) => state.payout);
  const [searchParameters] = useSearchParams();
  const accountCustomId = searchParameters.get('accountCustomId');
  const [selectedTab, setSelectedTab] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'stations' | 'devices' | 'online' | 'offline' | null>(
    null,
  );

  const [showSettings, setShowSettings] = useState(false);
  const [accountManagementData, setAccountManagementData] =
    useState<AccountManagementDataProperties>({
      totalDevices: 0,
      totalStations: 0,
      online: 0,
      offline: 0,
    });
  const [accountDetails, setAccountDetails] = useState<AccountDetailsDataProperties>({
    accountCustomId: '',
    accountName: '',
    email: '',
  });

  /**
   * fetch Account  Data show
   */
  const fetchAccountData = async () => {
    if (!accountCustomId) {
      throw new Error('accountId is required');
    }
    try {
      const response = await account.fetchDeviceCountReport(accountCustomId, { hardRefresh: true });
      setAccountManagementData(response?.data?.finalResult);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || t('somethingWentWrong'),
          },
        }),
      );
    }
  };

  /**
   * Fetch Account Details  Data
   */
  const fetchAccountDetails = async () => {
    if (!accountCustomId) {
      throw new Error('accountId is required');
    }
    try {
      const response = await account.fetchAccountDetails(accountCustomId);
      setAccountDetails(response.data);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || t('somethingWentWrong'),
          },
        }),
      );
    }
  };

  const stationDeviceItems = [
    {
      key: 'stations' as const,
      label: 'stations',
      count: accountManagementData.totalStations,
    },
    {
      key: 'devices' as const,
      label: 'devices',
      count: accountManagementData.totalDevices,
    },
  ];

  useEffect(() => {
    fetchAccountData();
    fetchAccountDetails();
    if (accountCustomId) {
      dispatch(fetchAccountRevenueTotal({ accountCustomId }));
    }
  }, []);

  // Fetch filters for the account on mount and clear them on unmount.
  useEffect(() => {
    if (accountCustomId) {
      dispatch(fetchByTransactionAccountFilters({ accountCustomId }));
    }
    // Cleanup on unmount: clears ByTransaction filters
    return () => {
      dispatch(clearByTransactionData());
      dispatch(clearByMonthData());
      dispatch(clearPayoutData());
    };
  }, [accountCustomId, dispatch]);
  useEffect(() => {
    dispatch(
      setPageTitle({
        title: t('partners'),
      }),
    );
  }, [t, dispatch]);
  return (
    <div className="accounts-management-payout">
      <div className="title-content" dir={i18n.dir()}>
        <span className="back-arrow" role="presentation" onClick={() => navigate(-1)}>
          <BackArrowIcon />
        </span>{' '}
        <PageTitle
          title={t('facilityDetails')}
          buttonSvgIcon={<SettingIcon />}
          buttonType="none"
          onClickButton={() => setShowSettings((previous) => !previous)}
        />
        {showSettings && (
          <div className="settings-dropdown">
            <SettingAccount setShowSettings={setShowSettings} />
          </div>
        )}
      </div>

      <div className="account-main-card">
        <div className="account-card">
          {/* Left Section */}
          <div className="account-info">
            <div className="info-row">
              <p className="label">{t('accountNumber')}:</p>
              <p className="value">{accountDetails?.accountCustomId ?? ''}</p>
            </div>
            <div className="info-row">
              <p className="label">{t('name')}:</p>
              <p className="value">{accountDetails?.accountName ?? ''}</p>
            </div>
            <div className="info-row">
              <p className="label">{t('email')}:</p>
              <p className="value">{accountDetails?.email ?? ''}</p>
            </div>
          </div>

          <div className="border"> </div>
          {/* Middle Section */}
          <div className="charger-status">
            <div
              className="status online"
              key="online"
              role="button"
              tabIndex={0}
              onClick={() => setActiveTab('online')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') setActiveTab('online');
              }}>
              {t('onlineCharger')} <span>{accountManagementData?.online ?? 0}</span>
            </div>

            <div
              className="status offline"
              key="offline"
              role="button"
              tabIndex={0}
              onClick={() => setActiveTab('offline')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') setActiveTab('offline');
              }}>
              {t('offlineCharger')} <span>{accountManagementData?.offline ?? 0}</span>
            </div>
          </div>

          {/* Right: Stations & Devices */}
          <div className="station-device">
            {stationDeviceItems.map(({ key, label, count }) => (
              <div
                key={key}
                className={`box ${activeTab === key ? 'active' : ''}`}
                role="button"
                tabIndex={0}
                onClick={() => setActiveTab(key)}
                onKeyDown={(even) => {
                  if (even.key === 'Enter' || even.key === ' ') setActiveTab(key);
                }}>
                <span>{t(label)}</span>
                <span className="count">{count}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="account-summary">
          <div className="summary-item">
            <span className="label">{t('totalRevenue')}</span>
            <div className="value-box">
              <p className="icon">{branding.text.currency}</p>
              <div className="line" />
              <span className="value">{ToFixedValue(revenueTotal?.totalRevenue || 0)}</span>
            </div>
          </div>

          <div className="summary-item">
            <span className="label">{t('totalPaidRevenue')}</span>
            <div className="value-box">
              <p className="icon">{branding.text.currency}</p>
              <div className="line" />
              <span className="value">{ToFixedValue(revenueTotal?.totalPaidRevenue || 0)}</span>
            </div>
          </div>
        </div>
        <div className="account-summary">
          <div className="summary-item">
            <span className="label">{t('payableAmount')}</span>
            <div className="value-box">
              <p className="icon">{branding.text.currency}</p>
              <div className="line" />
              <span className="value">
                {ToFixedValue(revenueTotal?.pendingBalanceFromLastPayout || 0)}
              </span>
            </div>
          </div>
          <div className="summary-item">
            <span className="label">{t('netPayableAmount')}</span>
            <div className="value-box">
              <p className="icon">{branding.text.currency}</p>
              <div className="line" />
              <span className="value">{ToFixedValue(revenueTotal?.pendingBalance || 0)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="chart-container">
        <RevenueVsPayableChart />
        <PowerConsumedChart />
      </div>

      <div className="bottom-line" />

      {/* Tabs */}
      <TabToggle
        tabs={[t('payout'), t('revenue')]}
        defaultTab={t('payout')}
        onChange={(tab) => setSelectedTab(tab)}
      />
      <div className="accounts-management-table">
        {selectedTab === t('payout') ? <Payout /> : <Revenue />}
      </div>

      {/* Stations / Devices Popup */}
      {activeTab && (
        <div className="stations-devices-container">
          {activeTab === 'stations' && (
            <div>
              <Stations onClose={() => setActiveTab(null)} />
            </div>
          )}
          {activeTab === 'devices' && (
            <div>
              <Devices onClose={() => setActiveTab(null)} />
            </div>
          )}
          {activeTab === 'online' && (
            <div>
              <Devices onClose={() => setActiveTab(null)} isDeviceOffline={false} />
            </div>
          )}

          {activeTab === 'offline' && (
            <div>
              <Devices onClose={() => setActiveTab(null)} isDeviceOffline />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AccountsManagementPayout;
